import {
  type AccountListEntry,
  type AuthLoginResult,
  type BucketEntry,
  type RoutineInstanceEntry,
  accountsExist,
  accountsList,
  allowanceGenerateDue,
  authLogout,
  cloudRestoreComplete,
  cloudSyncFamily,
  deviceGetId,
  familyContentPolicyStatus,
  getStartupError,
  routineInstancesToday,
  routinesGenerateToday,
  runtimePlatform,
} from '@chorepop/core/client';
import { isTestDataBuild } from '@chorepop/core/client';
import type { Session } from '@supabase/supabase-js';
import {
  type Component,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { primeCoinJingleAudio } from './audio/rewardJingle';
import {
  type CloudAuthProvider,
  type CloudGuardianBootstrap,
  type EmailTrialCredentials,
  type EmailTrialTotpEnrollment,
  type MobilePlatform,
  beginCloudSignIn,
  beginEmailTrialSignIn,
  beginEmailTrialSignUp,
  bootstrapCloudGuardian,
  cancelFamilyDataDeletion,
  detectMobilePlatform,
  enrollEmailTrialTotp,
  fetchCloudFamilySyncPayload,
  getCloudAuthProjectUrl,
  getCloudAuthPublishableKey,
  getCloudSession,
  getEmailTrialMfaStatus,
  getFamilyContentSafetyState,
  getFamilyDataDeletionRecovery,
  isCloudAuthConfigured,
  listenForCloudAuthCallbacks,
  providersForPlatform,
  signOutCloudSession,
  startOrResumeCloudTrial,
  verifyEmailTrialTotp,
} from './cloud/cloudAuth';
import type { TrialSignInMethod } from './cloud/trialAuthPolicy';
import { useBackNavigation } from './hooks/useBackNavigation';
import { childHomeTabAfterLogin } from './lib/postLoginRoute';
import { openChildPrivacyInformation } from './lib/privacyLinks';
import { REPORT_CONCERN_EVENT, type ReportConcernContext } from './lib/reportConcern';
import { AccountSelectionScreen } from './screens/AccountSelectionScreen';
import { AddBucketScreen } from './screens/AddBucketScreen';
import { AddChildScreen } from './screens/AddChildScreen';
import { AddGuardianScreen } from './screens/AddGuardianScreen';
import { AllowanceScreen } from './screens/AllowanceScreen';
import { AuditLogScreen } from './screens/AuditLogScreen';
import { AuthenticatedShell } from './screens/AuthenticatedShell';
import { BucketAdjustBalanceScreen } from './screens/BucketAdjustBalanceScreen';
import { BucketManagerScreen } from './screens/BucketManagerScreen';
import { BucketTemplatesScreen } from './screens/BucketTemplatesScreen';
import { ChildDetailScreen } from './screens/ChildDetailScreen';
import { ChoreTrendsScreen } from './screens/ChoreTrendsScreen';
import { ChoresScreen } from './screens/ChoresScreen';
import { CreateChoreScreen } from './screens/CreateChoreScreen';
import { CreateRoutineScreen } from './screens/CreateRoutineScreen';
import { DeactivateBucketScreen } from './screens/DeactivateBucketScreen';
import { DeleteBucketScreen } from './screens/DeleteBucketScreen';
import { EditBucketScreen } from './screens/EditBucketScreen';
import { EditChoreScreen } from './screens/EditChoreScreen';
import { FamilyContentRulesScreen } from './screens/FamilyContentRulesScreen';
import { FeedbackScreen } from './screens/FeedbackScreen';
import { GiftAllocationScreen } from './screens/GiftAllocationScreen';
import { GoalsScreen } from './screens/GoalsScreen';
import { GuardianBucketTransferScreen } from './screens/GuardianBucketTransferScreen';
import { GuardianKidsScreen } from './screens/GuardianKidsScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { HomeScreen } from './screens/HomeScreen';
import { LoginPasswordScreen } from './screens/LoginPasswordScreen';
import { MobileGuardianHomeScreen } from './screens/MobileGuardianHomeScreen';
import { MonthlyStatementScreen } from './screens/MonthlyStatementScreen';
import { MoveMoneyScreen } from './screens/MoveMoneyScreen';
import { MyPendingRequestsScreen } from './screens/MyPendingRequestsScreen';
import { PendingApprovalsScreen } from './screens/PendingApprovalsScreen';
import { RecordDepositScreen } from './screens/RecordDepositScreen';
import { RecordGiftScreen } from './screens/RecordGiftScreen';
import { ReportConcernScreen } from './screens/ReportConcernScreen';
import { ResetChildPasswordScreen } from './screens/ResetChildPasswordScreen';
import { RestoreDevicePasswordScreen } from './screens/RestoreDevicePasswordScreen';
import { ReviewBucketsScreen } from './screens/ReviewBucketsScreen';
import { RoutineHistoryScreen } from './screens/RoutineHistoryScreen';
import { RoutineManagementScreen } from './screens/RoutineManagementScreen';
import { RoutineTaskScreen } from './screens/RoutineTaskScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { type SetupCloudIdentity, type SetupCloudStatus, SetupScreen } from './screens/SetupScreen';
import { SpendScreen } from './screens/SpendScreen';
import { StreakHistoryScreen } from './screens/StreakHistoryScreen';
import { SubmitPendingDepositScreen } from './screens/SubmitPendingDepositScreen';
import { SubmitPendingWithdrawalScreen } from './screens/SubmitPendingWithdrawalScreen';
import { SubscriptionRequiredScreen } from './screens/SubscriptionRequiredScreen';
import { TutorialsScreen } from './screens/TutorialsScreen';
import { highContrastEnabled, largeFontEnabled } from './state/a11yPreferences';
import {
  activateAppearanceForAccount,
  clearActiveAppearanceAccount,
} from './state/accountAppearance';
import { hideFamilyContent, setFamilyContentViewer } from './state/familyContentVisibility';
import { mobilePreviewEnabled, setMobilePreviewEnabled } from './state/mobilePreview';
import { palette } from './state/palettePreference';
import { themeMode } from './state/themePreference';
import './styles/app.css';
import './styles/themes.css';

// State machine for the top-level route. Each variant carries the data
// the screen needs to render -- no orphaned signals scattered across the
// component tree.
type SuccessSession = Extract<AuthLoginResult, { outcome: 'success' }>;
type ReadyCloudGuardian = Extract<CloudGuardianBootstrap, { kind: 'ready' }>;

type PendingCloudContext = {
  account: AccountListEntry;
  accessToken: string;
  bootstrap: ReadyCloudGuardian;
};

type Route =
  | { name: 'loading' }
  | { name: 'setup' }
  | { name: 'restoreDevicePassword'; account: AccountListEntry; cloud: PendingCloudContext }
  | {
      name: 'selection';
      welcomeMessage?: string;
      pendingIntent?: 'addChild' | 'settings';
    }
  | {
      name: 'login';
      account: AccountListEntry;
      nextIntent?: 'addChild' | 'settings' | 'subscribeNow';
    }
  | { name: 'home'; account: AccountListEntry; session: SuccessSession; flashMessage?: string }
  | { name: 'kids'; account: AccountListEntry; session: SuccessSession }
  | {
      // US-UI-MOBILE-LAYOUT -- mobile-only detail screen reached from
      // the mobile master list. Carries the selected child's id +
      // display name so ChildDetailScreen can render without
      // re-fetching from URL params.
      name: 'childDetail';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      flashMessage?: string;
      returnRoute?: 'home' | 'kids';
    }
  | { name: 'addChild'; account: AccountListEntry; session: SuccessSession }
  | {
      // US-BUCKET-02 -- interstitial after successful child creation,
      // letting the guardian review and adjust the default buckets
      // before the child starts using them. Routes to home when done.
      name: 'reviewBuckets';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
    }
  | { name: 'addGuardian'; account: AccountListEntry; session: SuccessSession }
  | {
      name: 'addBucket';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      existingBuckets: BucketEntry[];
    }
  | {
      name: 'editBucket';
      account: AccountListEntry;
      session: SuccessSession;
      bucket: BucketEntry;
      existingBuckets: BucketEntry[];
    }
  | {
      name: 'deactivateBucket';
      account: AccountListEntry;
      session: SuccessSession;
      bucket: BucketEntry;
      existingBuckets: BucketEntry[];
    }
  | {
      name: 'deleteBucket';
      account: AccountListEntry;
      session: SuccessSession;
      bucket: BucketEntry;
      existingBuckets: BucketEntry[];
    }
  | {
      name: 'resetChildPassword';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | {
      name: 'recordDeposit';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | {
      name: 'recordGift';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | {
      name: 'giftAllocation';
      account: AccountListEntry;
      session: SuccessSession;
      depositId?: number;
    }
  | { name: 'spend'; account: AccountListEntry; session: SuccessSession; bucket: BucketEntry }
  | {
      name: 'submitPendingWithdrawal';
      account: AccountListEntry;
      session: SuccessSession;
      bucket: BucketEntry;
    }
  | {
      // US-BUCKET-09 -- child opens "Move money" to submit a transfer
      // request between two of their own buckets.
      name: 'moveMoney';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      buckets: BucketEntry[];
      currencyCode: string;
    }
  | {
      name: 'history';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | {
      name: 'auditLog';
      account: AccountListEntry;
      session: SuccessSession;
    }
  | {
      name: 'chores';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number | undefined;
      childDisplayName: string;
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | {
      name: 'createChore';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      /** When true, the form opens in master-list ("any child") mode. */
      defaultAnyChild?: boolean;
      /** Where to navigate on cancel or creation. Defaults to 'chores'. */
      returnRoute?: 'home' | 'chores';
      /**
       * The Chores view to return to after creation. This is deliberately
       * separate from the form's initial child id: the top-level All Chores
       * flow starts with the guardian id as a harmless selector placeholder,
       * but must return to the all-children view rather than querying the
       * guardian as if they were a child.
       */
      returnChoreScope?: { childAccountId: number | undefined; childDisplayName: string };
    }
  | {
      name: 'editChore';
      account: AccountListEntry;
      session: SuccessSession;
      chore: import('@chorepop/core/client').ChoreEntry;
      childAccountId: number;
      childDisplayName: string;
    }
  | { name: 'submitPendingDeposit'; account: AccountListEntry; session: SuccessSession }
  | {
      name: 'myRequests';
      account: AccountListEntry;
      session: SuccessSession;
      flashMessage?: string;
    }
  | {
      name: 'pendingApprovals';
      account: AccountListEntry;
      session: SuccessSession;
      flashMessage?: string;
      /**
       * US-UI-APPROVALS -- when present, the queue is filtered to items
       * for this child only. Set when the user clicks a per-child
       * Approvals button on Home; absent when they click the global
       * bell. The filter is applied in the screen, not on the wire --
       * the existing list endpoints don't take a child filter.
       */
      filterChildId?: number;
    }
  | { name: 'settings'; account: AccountListEntry; session: SuccessSession }
  | {
      name: 'familyContentRules';
      account: AccountListEntry;
      session: SuccessSession;
      policyVersion: string;
      nextIntent?: 'addChild' | 'settings' | 'subscribeNow';
    }
  | {
      name: 'subscriptionRequired';
      account: AccountListEntry;
      session: SuccessSession;
      cloudSession: Session;
      cloudFamilyId: string | null;
      subscribeNow?: boolean;
    }
  | { name: 'tutorials'; account: AccountListEntry; session: SuccessSession }
  | { name: 'feedback'; account: AccountListEntry; session: SuccessSession }
  | {
      name: 'reportConcern';
      account: AccountListEntry;
      session: SuccessSession;
      context?: ReportConcernContext;
    }
  | { name: 'routineManagement'; account: AccountListEntry; session: SuccessSession }
  | {
      name: 'streakHistory';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      returnToChildDetail: boolean;
    }
  | {
      name: 'createRoutine';
      account: AccountListEntry;
      session: SuccessSession;
      child: AccountListEntry;
      childBuckets: BucketEntry[];
      allChildren?: AccountListEntry[];
      routine?: import('@chorepop/core/client').RoutineEntry;
    }
  | {
      name: 'routineHistory';
      account: AccountListEntry;
      session: SuccessSession;
      task: import('@chorepop/core/client').RoutineEntry;
    }
  | {
      name: 'routineTask';
      account: AccountListEntry;
      session: SuccessSession;
      instanceId: number;
    }
  | {
      name: 'allowance';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId?: number;
      childDisplayName?: string;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | { name: 'goals'; account: AccountListEntry; session: SuccessSession }
  | { name: 'statement'; account: AccountListEntry; session: SuccessSession }
  | { name: 'bucketTemplates'; account: AccountListEntry; session: SuccessSession }
  | { name: 'choreTrends'; account: AccountListEntry; session: SuccessSession }
  | { name: 'givingGoals'; account: AccountListEntry; session: SuccessSession }
  | {
      // US-BUCKET-15 / US-BUCKET-17 -- guardian directly sets a bucket's balance
      name: 'adjustBucketBalance';
      account: AccountListEntry;
      session: SuccessSession;
      bucket: BucketEntry;
      sourceCode: 'balance_adjustment' | 'initial_balance';
    }
  | {
      // US-BUCKET-16 -- guardian instant transfer between two buckets
      name: 'guardianBucketTransfer';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      buckets: BucketEntry[];
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
      initialSourceBucketId?: number;
    }
  | {
      // US-BUCKET-18 -- guardian edits all deposit split percentages at once
      name: 'editBucketSplit';
      account: AccountListEntry;
      session: SuccessSession;
      childAccountId: number;
      childDisplayName: string;
      buckets: BucketEntry[];
      returnToChildDetail?: boolean;
      childDetailReturnRoute?: 'home' | 'kids';
    }
  | { name: 'error'; message: string };

// US-ROUTINE-03 -- Loads a single instance by id then renders RoutineTaskScreen.
const RoutineTaskLoader: Component<{
  sessionId: string;
  instanceId: number;
  currency: string;
  onDone: () => void;
  onBack: () => void;
}> = (props) => {
  const [instance] = createResource(async () => {
    const r = await routineInstancesToday(props.sessionId);
    if (!r.ok) return null;
    return r.value.find((i: RoutineInstanceEntry) => i.instanceId === props.instanceId) ?? null;
  });
  return (
    <Show when={instance()}>
      {(inst: () => RoutineInstanceEntry) => (
        <RoutineTaskScreen
          sessionId={props.sessionId}
          instance={inst()}
          currency={props.currency}
          onSubmitted={props.onDone}
          onBack={props.onBack}
        />
      )}
    </Show>
  );
};

export const App: Component = () => {
  // Prime Web Audio from the first genuine app interaction (including the
  // child's sign-in tap). Waiting until Home mounts is too late on iOS because
  // the gesture that opened Home has already finished.
  onMount(() => {
    const primeRewardAudio = () => primeCoinJingleAudio();
    window.addEventListener('pointerdown', primeRewardAudio, { passive: true, capture: true });
    window.addEventListener('keydown', primeRewardAudio, { capture: true });
    onCleanup(() => {
      window.removeEventListener('pointerdown', primeRewardAudio, { capture: true });
      window.removeEventListener('keydown', primeRewardAudio, { capture: true });
    });
  });

  // Browser UA detection is only a fallback. iPadOS may report a desktop UA
  // from WKWebView, while the native host can state the actual OS exactly.
  const [mobilePlatform, setMobilePlatform] = createSignal<MobilePlatform>(detectMobilePlatform());
  onMount(() => {
    void runtimePlatform().then((result) => {
      if (!result.ok) return;
      if (result.value === 'ios' || result.value === 'android') {
        setMobilePlatform(result.value);
      }
    });
  });

  // Check for migration failure at startup.  The Rust backend snapshots the
  // database before running migrations and restores it on failure, then
  // stores the error message so we can show a blocking error screen here.
  const [startupError, setStartupError] = createSignal<string | null>(null);
  onMount(async () => {
    const err = await getStartupError();
    if (err) setStartupError(err);
  });

  // Bridge the Rust background-checker event to a window CustomEvent so
  // any screen (e.g. HomeScreen) can listen without a direct Tauri dep.
  // The checker emits 'chorepop://sync-applied' after every successful
  // background snapshot apply; we forward it as 'chorepop:sync-applied'.
  onMount(() => {
    let unlisten: (() => void) | undefined;
    import('@tauri-apps/api/event')
      .then(({ listen }) => {
        listen<number>('chorepop://sync-applied', () => {
          window.dispatchEvent(new CustomEvent('chorepop:sync-applied'));
        }).then((fn) => {
          unlisten = fn;
        });
      })
      .catch(() => {
        /* non-Tauri env (browser dev) — ignore */
      });
    onCleanup(() => unlisten?.());
  });
  // US-PLATFORM-07 -- write the active theme to <html data-theme="...">
  // so the design-token overrides at `:root[data-theme="dark"]`
  // in styles/tokens.css activate.
  createEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = themeMode();
      document.documentElement.dataset.palette = palette();
    }
  });

  // Route managed by user actions; null until first user action moves us
  // off the initial-resource-driven route.
  const [manualRoute, setManualRoute] = createSignal<Route | null>(null);
  const [reportReturnRoute, setReportReturnRoute] = createSignal<Route | null>(null);
  onMount(() => {
    const environment = (
      import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }
    ).env;
    if (environment?.DEV !== true || !location.search.includes('chorepopScreenAudit=1')) return;

    // Browser-only responsive acceptance hook. It is unavailable in production
    // builds and only activates for the explicit audit URL. This lets the E2E
    // matrix render every real App route with its normal shell without adding
    // test navigation or a hidden screen gallery to the shipped application.
    const auditWindow = window as Window & {
      // biome-ignore lint/style/useNamingConvention: E2E-only global is intentionally namespaced.
      __CHOREPOP_E2E_SET_ROUTE__?: ((nextRoute: unknown) => void) | undefined;
    };
    auditWindow.__CHOREPOP_E2E_SET_ROUTE__ = (nextRoute) => setManualRoute(nextRoute as Route);
    onCleanup(() => {
      auditWindow.__CHOREPOP_E2E_SET_ROUTE__ = undefined;
    });
  });
  onMount(() => {
    const handleSessionBackgrounded = () => {
      setManualRoute({
        name: 'selection',
        welcomeMessage: 'You were signed out. Please sign in again.',
      });
    };
    window.addEventListener('chorepop:session-backgrounded', handleSessionBackgrounded);
    onCleanup(() =>
      window.removeEventListener('chorepop:session-backgrounded', handleSessionBackgrounded),
    );
  });
  const [childHomeTab, setChildHomeTab] = createSignal<'chores' | 'money' | 'activity'>('chores');
  const [cloudStatus, setCloudStatus] = createSignal<
    'idle' | 'opening_provider' | 'verifying' | 'link_required'
  >('idle');
  const [cloudMessage, setCloudMessage] = createSignal<string | null>(null);
  // First-run setup has a different cloud handshake from an existing
  // guardian login: the returned identity is held by the wizard while the
  // guardian chooses a local password and family settings.
  const [pendingSetupMethod, setPendingSetupMethod] = createSignal<TrialSignInMethod | null>(null);
  const [setupCloudIdentity, setSetupCloudIdentity] = createSignal<SetupCloudIdentity | null>(null);
  const [setupTotpChallenge, setSetupTotpChallenge] = createSignal<{
    factorId: string;
    enrollment?: EmailTrialTotpEnrollment;
  } | null>(null);
  const setupCloudStatus = (): SetupCloudStatus => {
    const status = cloudStatus();
    return status === 'link_required' ? 'error' : status;
  };

  // Hardware back button: show a logout confirmation when at the home
  // screen, otherwise navigate to the previous logical screen.
  const [showLogoutConfirm, setShowLogoutConfirm] = createSignal(false);

  // US-AUTH-09 -- guardian-picker modal for multi-guardian families.
  // When the user clicks a picker-footer action ("+ Add a child" /
  // Settings) and there are 2+ guardians, this
  // signal carries the chosen intent and the filtered guardian list.
  // The Selection screen renders an overlay modal when this is set.
  // Single-guardian families never see this -- startIntentFlow's
  // shortcut routes them straight to login.
  const [guardianPickerIntent, setGuardianPickerIntent] = createSignal<{
    intent: 'addChild' | 'settings';
    guardians: AccountListEntry[];
  } | null>(null);

  const [initialRoute] = createResource<Route>(async () => {
    const result = await accountsExist();
    if (result.ok) {
      return result.value ? { name: 'selection' } : { name: 'setup' };
    }
    return {
      name: 'error',
      message: `Could not check for existing accounts: ${result.error.message}`,
    };
  });

  const route = (): Route => {
    const current = manualRoute() ?? initialRoute() ?? { name: 'loading' as const };
    if (typeof document !== 'undefined') {
      document.body.dataset.chorepopRole = 'account' in current ? current.account.roleCode : '';
      document.body.dataset.chorepopRoute = current.name;
    }
    return current;
  };

  // Account display/accessibility preferences deliberately follow the active
  // local profile, never the last person who changed a setting on this device.
  createEffect(() => {
    const current = route();
    if ('account' in current) {
      activateAppearanceForAccount(current.account.accountId);
    } else {
      clearActiveAppearanceAccount();
    }
  });

  // The responsive shell owns the visual navigation, while the app state
  // machine remains the single routing authority. Keeping this bridge here
  // lets every authenticated screen share the same mobile tabs without
  // duplicating route wiring in each screen wrapper.
  onMount(() => {
    const handleChildHomeTab = (event: Event) => {
      const tab = (event as CustomEvent<string>).detail;
      if (tab === 'chores' || tab === 'money' || tab === 'activity') {
        // HomeScreen can change views itself (for example while playing the
        // money-arrival animation). Keep the route-owned initial tab in lock
        // step so a later remount cannot restore a different selected tab.
        setChildHomeTab(tab);
      }
    };
    const handleMobileNavigation = (event: Event) => {
      const tab = (event as CustomEvent<{ tab?: string }>).detail?.tab;
      const current = route();
      if (!tab || !('account' in current) || !('session' in current)) return;

      const account = current.account;
      const session = current.session;
      if (tab === 'signout') {
        setShowLogoutConfirm(true);
        return;
      }
      if (tab === 'settings') {
        setManualRoute({ name: 'settings', account, session });
        return;
      }
      if (tab === 'tutorials') {
        setManualRoute({ name: 'tutorials', account, session });
        return;
      }
      if (tab === 'privacy' && account.roleCode === 'child') {
        void openChildPrivacyInformation().catch(() => {
          // The public address remains documented in the child privacy page and support materials;
          // a device without a working external browser must not crash the signed-in session.
        });
        return;
      }
      if (tab === 'feedback' && account.roleCode === 'guardian') {
        setManualRoute({ name: 'feedback', account, session });
        return;
      }
      if (tab === 'report') {
        setReportReturnRoute(current);
        setManualRoute({ name: 'reportConcern', account, session });
        return;
      }
      if (tab === 'routines') {
        setManualRoute({ name: 'routineManagement', account, session });
        return;
      }
      if (tab === 'statements') {
        setManualRoute({ name: 'statement', account, session });
        return;
      }
      if (tab === 'history') {
        if (account.roleCode === 'guardian') {
          setManualRoute({ name: 'auditLog', account, session });
        } else {
          setManualRoute({
            name: 'history',
            account,
            session,
            childAccountId: account.accountId,
            childDisplayName: account.displayName,
          });
        }
        return;
      }
      if (tab === 'audit' && account.roleCode === 'guardian') {
        setManualRoute({ name: 'auditLog', account, session });
        return;
      }
      // Guardians manage balances and buckets from the child workspace. Keep
      // the overflow label useful without creating a duplicate, incomplete
      // money page or silently falling back to Family Overview.
      if (tab === 'money' && account.roleCode === 'guardian') {
        setManualRoute({ name: 'kids', account, session });
        return;
      }
      if (tab === 'goals' && account.roleCode === 'child') {
        setManualRoute({ name: 'goals', account, session });
        return;
      }
      if (tab === 'approvals') {
        setManualRoute(
          account.roleCode === 'guardian'
            ? { name: 'pendingApprovals', account, session }
            : { name: 'myRequests', account, session },
        );
        return;
      }
      if (tab === 'kids' && account.roleCode === 'guardian') {
        setManualRoute({ name: 'kids', account, session });
        return;
      }
      // The child tabs are views of the playful child dashboard. Keep them
      // inside HomeScreen instead of sending the child into the guardian
      // chores/history routes. This also keeps the bottom navigation and the
      // supplied Claude Design child layouts in sync.
      if (account.roleCode === 'child' && ['chores', 'money', 'activity'].includes(tab)) {
        const childTab = tab as 'chores' | 'money' | 'activity';
        setChildHomeTab(childTab);
        if (current.name === 'home') {
          // The dashboard is already mounted. Update it directly instead of
          // routing through Home again, which can lose an event during a
          // component remount on Android/iOS.
          window.dispatchEvent(new CustomEvent('chorepop:child-home-tab', { detail: childTab }));
        } else {
          setManualRoute({ name: 'home', account, session });
        }
        return;
      }
      if (tab === 'chores') {
        setManualRoute({
          name: 'chores',
          account,
          session,
          childAccountId: account.roleCode === 'child' ? account.accountId : undefined,
          childDisplayName: account.roleCode === 'child' ? account.displayName : 'All Chores',
        });
        return;
      }
      setManualRoute({ name: 'home', account, session });
    };

    const handleReportConcern = (event: Event) => {
      const context = (event as CustomEvent<ReportConcernContext>).detail;
      const current = route();
      if (!context || !('account' in current) || !('session' in current)) return;
      setReportReturnRoute(current);
      setManualRoute({
        name: 'reportConcern',
        account: current.account,
        session: current.session,
        context,
      });
    };

    window.addEventListener('chorepop:child-home-tab', handleChildHomeTab);
    window.addEventListener('chorepop:mobile-navigation', handleMobileNavigation);
    window.addEventListener(REPORT_CONCERN_EVENT, handleReportConcern);
    onCleanup(() => {
      window.removeEventListener('chorepop:child-home-tab', handleChildHomeTab);
      window.removeEventListener('chorepop:mobile-navigation', handleMobileNavigation);
      window.removeEventListener(REPORT_CONCERN_EVENT, handleReportConcern);
    });
  });

  const completeLogin = async (
    account: AccountListEntry,
    session: SuccessSession,
    nextIntent?: 'addChild' | 'settings' | 'subscribeNow',
  ) => {
    setFamilyContentViewer(account.accountId);
    const policyStatus = await familyContentPolicyStatus(session.sessionId);
    if (!policyStatus.ok) {
      await authLogout({ sessionId: session.sessionId });
      setManualRoute({
        name: 'selection',
        welcomeMessage: `ChorePop could not check the family safety rules: ${policyStatus.error.message}`,
      });
      return;
    }
    const needsFamilyTerms =
      account.roleCode === 'guardian' && !policyStatus.value.familyTermsAccepted;
    const needsChildRules = account.roleCode === 'child' && !policyStatus.value.childRulesAccepted;
    if (needsFamilyTerms || needsChildRules) {
      setManualRoute({
        name: 'familyContentRules',
        account,
        session,
        policyVersion: needsFamilyTerms
          ? policyStatus.value.familyTermsVersion
          : policyStatus.value.childRulesVersion,
        ...(nextIntent ? { nextIntent } : {}),
      });
      return;
    }
    if (isCloudAuthConfigured()) {
      try {
        const cloudSession = await getCloudSession();
        if (cloudSession) {
          const safetyState = await getFamilyContentSafetyState();
          for (const directive of safetyState) {
            if (
              directive.contentLocalId &&
              (directive.action === 'text_redacted' || directive.action === 'content_removed')
            ) {
              hideFamilyContent(directive.contentLocalId);
            }
          }
          const accessBlocked = safetyState.some(
            (directive) =>
              directive.familyLocked ||
              (directive.action === 'account_suspended' &&
                directive.targetedLocalAccountId === account.accountId),
          );
          if (accessBlocked) {
            await authLogout({ sessionId: session.sessionId });
            setManualRoute({
              name: 'selection',
              welcomeMessage:
                'This account is temporarily unavailable while ChorePop reviews a family safety report. Ask a guardian to contact support@chorepop.com.',
            });
            return;
          }
          const device = await deviceGetId();
          if (!device.ok) throw new Error(device.error.message);
          const trial = await startOrResumeCloudTrial(device.value);
          if (trial.lifecycleState === 'trial_active' && nextIntent === 'subscribeNow') {
            setManualRoute({
              name: 'subscriptionRequired',
              account,
              session,
              cloudSession,
              cloudFamilyId: null,
              subscribeNow: true,
            });
            return;
          }
          if (trial.lifecycleState === 'subscription_required') {
            setManualRoute({
              name: 'subscriptionRequired',
              account,
              session,
              cloudSession,
              cloudFamilyId: trial.cloudFamilyId,
            });
            return;
          }
          if (trial.lifecycleState === 'deletion_requested') {
            await authLogout({ sessionId: session.sessionId });
            setManualRoute({
              name: 'selection',
              welcomeMessage: 'This ChorePop trial has a pending data deletion request.',
            });
            return;
          }
        }
      } catch (error) {
        await authLogout({ sessionId: session.sessionId });
        setManualRoute({
          name: 'selection',
          welcomeMessage:
            error instanceof Error
              ? `ChorePop could not verify this trial: ${error.message}`
              : 'ChorePop could not verify this trial.',
        });
        return;
      }
    }
    await routinesGenerateToday(session.sessionId);
    const initialChildTab = childHomeTabAfterLogin(account.roleCode);
    if (initialChildTab) setChildHomeTab(initialChildTab);
    if (account.roleCode === 'guardian') void allowanceGenerateDue(session.sessionId);
    if (nextIntent === 'addChild') {
      setManualRoute({ name: 'addChild', account, session });
    } else if (nextIntent === 'settings') {
      setManualRoute({ name: 'settings', account, session });
    } else {
      setManualRoute({ name: 'home', account, session });
    }
  };

  const syncAndPrepareLinkedFamily = async (session: Session, bootstrap: ReadyCloudGuardian) => {
    setCloudStatus('verifying');
    setCloudMessage('Syncing your family data…');
    const payload = await fetchCloudFamilySyncPayload(bootstrap.family.id);
    const sync = await cloudSyncFamily({
      accessToken: session.access_token,
      publishableKey: getCloudAuthPublishableKey(),
      supabaseUrl: getCloudAuthProjectUrl(),
      familyId: bootstrap.family.id,
      familyAccountId: bootstrap.guardianAccount.id,
      importId: payload.importId,
      rows: payload.rows,
    });
    if (!sync.ok) throw new Error(sync.error.message);
    const listed = await accountsList();
    if (!listed.ok) throw new Error(listed.error.message);
    const account = listed.value.find(
      (candidate) => candidate.accountId === sync.value.localAccountId,
    );
    if (!account) throw new Error('The synced guardian account was not available on this device.');
    const context: PendingCloudContext = {
      account,
      accessToken: session.access_token,
      bootstrap,
    };
    setPendingSetupMethod(null);
    setSetupTotpChallenge(null);
    setCloudMessage(null);
    setCloudStatus('idle');
    // OAuth proves this is the guardian's cloud family, but never unlocks a
    // device. The next route establishes a new device-local password before
    // it mints the first local session.
    setManualRoute({ name: 'restoreDevicePassword', account, cloud: context });
  };

  const startValidatedLocalTrial = async (
    session: Session,
    method: TrialSignInMethod,
    email: string,
  ) => {
    const recovery = await getFamilyDataDeletionRecovery();
    if (recovery) await cancelFamilyDataDeletion(recovery.familyId);
    const device = await deviceGetId();
    if (!device.ok) throw new Error(device.error.message);
    const trial = await startOrResumeCloudTrial(device.value);
    if (trial.lifecycleState === 'trial_active') {
      setSetupCloudIdentity({ method, email });
      setSetupTotpChallenge(null);
      setPendingSetupMethod(null);
      setCloudStatus('idle');
      return;
    }
    if (trial.lifecycleState === 'subscription_required') {
      throw new Error('This trial has ended. Subscribe, download your data, or request deletion.');
    }
    if (trial.lifecycleState === 'deletion_requested') {
      throw new Error('This ChorePop account has a pending deletion request.');
    }
    if (!trial.cloudFamilyId) {
      throw new Error('The subscription does not have a cloud family yet. Please try again later.');
    }
    const bootstrap = await bootstrapCloudGuardian({ expectedUserId: session.user.id });
    if (bootstrap.kind !== 'ready') {
      throw new Error('The subscribed ChorePop family was not ready to open.');
    }
    await syncAndPrepareLinkedFamily(session, bootstrap);
  };

  const requireEmailTrialTotp = async (): Promise<boolean> => {
    const status = await getEmailTrialMfaStatus();
    if (!status.emailVerified) {
      throw new Error('Check your email for the confirmation link before starting ChorePop.');
    }
    if (!status.verifiedTotpFactorId) {
      const enrollment = await enrollEmailTrialTotp();
      setSetupTotpChallenge({ factorId: enrollment.factorId, enrollment });
      setCloudStatus('idle');
      return false;
    }
    if (status.currentLevel !== 'aal2') {
      setSetupTotpChallenge({ factorId: status.verifiedTotpFactorId });
      setCloudStatus('idle');
      return false;
    }
    return true;
  };

  // Native mobile providers return a Supabase session directly after the
  // platform credential sheet completes. Browser/deep-link callbacks use this
  // same hand-off so guardian linking and first-run setup stay consistent.
  const handleCloudSession = async (session: Session) => {
    const setupMethod = pendingSetupMethod();
    if (setupMethod) {
      setCloudStatus('verifying');
      setCloudMessage(null);
      const bootstrap = await bootstrapCloudGuardian({ expectedUserId: session.user.id });
      if (bootstrap.kind === 'unlinked') {
        const email = bootstrap.user.email?.trim();
        if (!email) throw new Error('This account did not provide an email address.');
        if (setupMethod === 'email_password' && !(await requireEmailTrialTotp())) return;
        await startValidatedLocalTrial(session, setupMethod, email);
        return;
      }
      if (bootstrap.kind === 'family_selection_required') {
        throw new Error('Choose a single ChorePop family before signing in on this device.');
      }
      await syncAndPrepareLinkedFamily(session, bootstrap);
      return;
    }
    setCloudStatus('idle');
    setCloudMessage('Cloud sign-in is only used to set up or restore a new device.');
  };

  const beginSetupCloudSignIn = async (provider: CloudAuthProvider) => {
    setPendingSetupMethod(provider);
    setSetupTotpChallenge(null);
    setCloudMessage(null);
    setCloudStatus('opening_provider');
    try {
      const session = await beginCloudSignIn(provider);
      if (session) await handleCloudSession(session);
    } catch (error: unknown) {
      setPendingSetupMethod(null);
      setCloudStatus('idle');
      setCloudMessage(describeCloudSignInError(error));
    }
  };

  const beginSetupEmailSignIn = async (
    input: EmailTrialCredentials,
    intent: 'sign_up' | 'sign_in',
  ) => {
    setPendingSetupMethod('email_password');
    setSetupTotpChallenge(null);
    setCloudMessage(null);
    setCloudStatus('opening_provider');
    try {
      if (intent === 'sign_up') {
        const result = await beginEmailTrialSignUp(input);
        if (!result.session) {
          setCloudStatus('idle');
          setCloudMessage('Check your email and open the confirmation link to continue.');
          return;
        }
        await handleCloudSession(result.session);
        return;
      }
      await handleCloudSession(await beginEmailTrialSignIn(input));
    } catch (error: unknown) {
      setPendingSetupMethod(null);
      setCloudStatus('idle');
      setCloudMessage(describeCloudSignInError(error));
    }
  };

  const verifySetupEmailTotp = async (factorId: string, code: string) => {
    setCloudStatus('verifying');
    setCloudMessage(null);
    try {
      await handleCloudSession(await verifyEmailTrialTotp({ factorId, code }));
    } catch (error: unknown) {
      setCloudStatus('idle');
      setCloudMessage(describeCloudSignInError(error));
    }
  };

  const clearCloudSignIn = async () => {
    setCloudStatus('idle');
    setCloudMessage(null);
    if (isCloudAuthConfigured()) {
      try {
        await signOutCloudSession();
      } catch {
        // Local navigation must still work if the device is offline.
      }
    }
  };

  const logoutLocal = async (sessionId: string) => {
    await authLogout({ sessionId });
  };

  // Handle the mobile PKCE callback at the app root. The Rust bridge verifies
  // the access token independently before it mints the local SQLite session.
  onMount(() => {
    if (!isCloudAuthConfigured()) return;
    let unlisten: (() => void) | undefined;
    void listenForCloudAuthCallbacks(
      (session) => {
        void handleCloudSession(session).catch((error: unknown) => {
          setPendingSetupMethod(null);
          setCloudStatus('idle');
          setCloudMessage(describeCloudSignInError(error));
        });
      },
      (error) => {
        setCloudStatus('idle');
        setCloudMessage(error.message);
      },
    ).then((stopListening) => {
      unlisten = stopListening;
    });
    onCleanup(() => unlisten?.());
  });

  // Route key for the back-navigation hook -- changes on every route transition.
  const routeKey = createMemo(() => route().name);

  // Back navigation: determines what "go back" means for the current route.
  // At the home/dashboard screen, show a logout confirmation instead of exiting.
  useBackNavigation(() => {
    const r = route();
    switch (r.name) {
      case 'home':
      case 'subscriptionRequired':
      case 'loading':
      case 'setup':
      case 'selection':
        // At the root screen -- ask to log out rather than exit.
        setShowLogoutConfirm(true);
        break;
      // Screens that have a logical "parent" -- navigate there directly.
      case 'chores':
      case 'history':
      case 'pendingApprovals':
        if ('account' in r && 'session' in r) {
          setManualRoute({ name: 'home', account: r.account, session: r.session });
        }
        break;
      case 'createChore':
        if ('account' in r && 'session' in r) {
          // Pool chores (any-child) always go back to home — there is no
          // per-child chores list to return to, and childAccountId is the
          // guardian's own account which would load nonsense.
          if (r.returnRoute === 'home' || r.defaultAnyChild) {
            setManualRoute({ name: 'home', account: r.account, session: r.session });
          } else {
            setManualRoute({
              name: 'chores',
              account: r.account,
              session: r.session,
              childAccountId: r.childAccountId,
              childDisplayName: r.childDisplayName,
            });
          }
        }
        break;
      case 'editChore':
        if ('account' in r && 'session' in r) {
          setManualRoute({
            name: 'chores',
            account: r.account,
            session: r.session,
            childAccountId: r.childAccountId,
            childDisplayName: r.childDisplayName,
          });
        }
        break;
      default:
        // For all other screens: go home if we have session context.
        if ('account' in r && 'session' in r) {
          setManualRoute({ name: 'home', account: r.account, session: r.session });
        }
        break;
    }
  }, routeKey());

  // US-DEV-TESTDATA -- fetch the build flavour once on mount.
  const [isTestData] = createResource(async () => {
    const r = await isTestDataBuild();
    if (!r.ok) return false;
    return r.value;
  });

  // US-UI-MOBILE-PREVIEW -- wrap the entire route Switch in a div
  // that applies the mobile-frame styles when the toggle is on.
  // The frame uses `data-viewport-mode="mobile"` so any width-based
  // styling can target it via attribute selector instead of relying
  // on viewport width. The exit chip in the corner lets the user
  // toggle off without leaving the framed view.
  return (
    <>
      {/* Migration failure screen -- shown when startup migrations failed and
          were automatically rolled back.  Blocks all other UI so the user
          can't interact with a potentially inconsistent database. */}
      <Show when={startupError()}>
        <div class="startup-error-overlay">
          <div class="startup-error-card">
            <h1 class="startup-error-title">⚠️ Upgrade failed</h1>
            <p class="startup-error-body">
              A database upgrade did not complete. Your data has been automatically restored to its
              previous state — nothing was lost.
            </p>
            <p class="startup-error-detail">{startupError()}</p>
            <p class="startup-error-body">
              Please wait for the next build and reinstall the app. If this device is a client, you
              can also sign in again to sync your family from the cloud.
            </p>
          </div>
        </div>
      </Show>
      {/* Hardware back button logout confirmation dialog.
          Shown when the user presses back from the home/dashboard screen. */}
      <Show when={showLogoutConfirm()}>
        {(() => {
          const r = route();
          const session = 'session' in r ? r.session : null;
          return (
            <div
              class="logout-confirm-scrim"
              role="dialog"
              aria-modal="true"
              aria-labelledby="logout-confirm-title"
            >
              <div class="logout-confirm-card">
                <h2 id="logout-confirm-title">Sign out?</h2>
                <p>Do you want to sign out of ChorePop?</p>
                <div class="logout-confirm-actions">
                  <button
                    type="button"
                    class="secondary logout-confirm-stay"
                    onClick={() => setShowLogoutConfirm(false)}
                  >
                    Stay
                  </button>
                  <button
                    type="button"
                    class="primary logout-confirm-submit"
                    onClick={async () => {
                      setShowLogoutConfirm(false);
                      if (session) {
                        try {
                          await logoutLocal(session.sessionId);
                        } catch {
                          /* ignore */
                        }
                      }
                      setManualRoute({ name: 'selection' });
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </Show>

      {/* US-DEV-TESTDATA -- non-dismissable orange banner. Always at
          the very top of every screen so a tester never confuses
          this build with their real install. High-contrast colour
          distinct from any production accent. */}
      <Show when={isTestData() === true}>
        <div
          role="alert"
          data-testid="test-data-banner"
          style="position: sticky; top: 0; z-index: 99999; background: #f97316; color: #1c1917; padding: 8px 16px; font-weight: 600; text-align: center; border-bottom: 2px solid #c2410c; font-size: 13px;"
        >
          TEST DATA BUILD -- do not use for real family money
        </div>
      </Show>
      <div
        class="mobile-preview-shell"
        data-mobile-preview={mobilePreviewEnabled() ? 'on' : 'off'}
        data-a11y-contrast={highContrastEnabled() ? 'high' : 'normal'}
        data-a11y-font-size={largeFontEnabled() ? 'large' : 'normal'}
        style={
          mobilePreviewEnabled()
            ? 'min-height: 100vh; background: #1f2937; display: flex; align-items: flex-start; justify-content: center; padding: 24px;'
            : ''
        }
      >
        <div
          data-viewport-mode={mobilePreviewEnabled() ? 'mobile' : 'desktop'}
          style={
            mobilePreviewEnabled()
              ? 'width: 390px; height: 844px; background: var(--surface-1, #fff); border-radius: 24px; overflow: auto; position: relative; box-shadow: 0 12px 36px rgba(0,0,0,0.35);'
              : ''
          }
        >
          <Show when={mobilePreviewEnabled()}>
            <button
              type="button"
              onClick={() => setMobilePreviewEnabled(false)}
              data-testid="mobile-preview-exit"
              style="position: absolute; top: 8px; right: 8px; z-index: 9999; background: rgba(0,0,0,0.6); color: #fff; border: 0; padding: 4px 8px; border-radius: 12px; font-size: 12px; cursor: pointer;"
              title="Exit mobile preview"
            >
              ✕ Exit preview
            </button>
          </Show>
          <Switch>
            <Match when={route().name === 'loading'}>
              <main class="app-loading">
                <p>Starting ChorePop…</p>
              </main>
            </Match>

            <Match when={route().name === 'setup'}>
              <SetupScreen
                onCreated={async (accountId, displayName, familyName, subscriptionChoice) => {
                  if (subscriptionChoice === 'subscribe_now') {
                    const listed = await accountsList();
                    const account = listed.ok
                      ? listed.value.find((candidate) => candidate.accountId === accountId)
                      : undefined;
                    if (account) {
                      setManualRoute({ name: 'login', account, nextIntent: 'subscribeNow' });
                      return;
                    }
                  }
                  setManualRoute({
                    name: 'selection',
                    welcomeMessage: `Welcome to ${familyName ?? 'your family'}, ${displayName}! Your account is ready.`,
                  });
                }}
                platform={mobilePlatform()}
                providers={providersForPlatform(mobilePlatform())}
                cloudStatus={setupCloudStatus()}
                cloudMessage={cloudMessage()}
                cloudIdentity={setupCloudIdentity()}
                onCloudSignIn={(provider) => void beginSetupCloudSignIn(provider)}
                onEmailSignIn={(input, intent) => void beginSetupEmailSignIn(input, intent)}
                totpChallenge={setupTotpChallenge()}
                onTotpVerify={(factorId, code) => void verifySetupEmailTotp(factorId, code)}
              />
            </Match>

            <Match when={route().name === 'selection'}>
              {(() => {
                const r = route();
                const startIntentFlow = async (intent: 'addChild' | 'settings') => {
                  // Need a guardian session. List accounts, pick a guardian.
                  const result = await accountsList();
                  if (!result.ok) {
                    setManualRoute({
                      name: 'selection',
                      welcomeMessage: 'Could not load accounts. Please try again.',
                    });
                    return;
                  }
                  const guardians = result.value.filter((a) => a.roleCode === 'guardian');
                  if (guardians.length === 0) {
                    setManualRoute({
                      name: 'selection',
                      welcomeMessage: 'No guardian account found. Please create one first.',
                    });
                    return;
                  }
                  if (guardians.length === 1) {
                    const sole = guardians[0];
                    if (!sole) return;
                    setManualRoute({ name: 'login', account: sole, nextIntent: intent });
                    return;
                  }
                  // US-AUTH-09 -- multiple guardians. Open the picker modal in
                  // place of bouncing back to the picker with a banner. The
                  // modal lives on top of the existing selection screen so
                  // the user keeps spatial context. Picking a guardian inside
                  // the modal forwards the intent onto the login route exactly
                  // as the single-guardian shortcut does above.
                  setGuardianPickerIntent({ intent, guardians });
                };

                return (
                  <AccountSelectionScreen
                    welcomeMessage={r.name === 'selection' ? r.welcomeMessage : undefined}
                    onAccountPicked={(account) => {
                      // BUG-018: forward any pendingIntent stashed on the selection
                      // route onto the new login route. If the user clicks a child
                      // tile while an intent is pending we don't forward -- the
                      // login screen's role check will reject and route back here,
                      // which is the correct behaviour for a clearly wrong pick.
                      //
                      // US-AUTH-09 made `pendingIntent` vestigial for the
                      // happy path (the modal handles it directly), but it
                      // still applies to any leftover route created before
                      // the modal was wired -- keep the forwarding logic.
                      const pending =
                        r.name === 'selection' && account.roleCode === 'guardian'
                          ? r.pendingIntent
                          : undefined;
                      setManualRoute(
                        pending
                          ? { name: 'login', account, nextIntent: pending }
                          : { name: 'login', account },
                      );
                    }}
                    onAddChild={() => void startIntentFlow('addChild')}
                    onOpenSettings={() => void startIntentFlow('settings')}
                    guardianPicker={(() => {
                      const gp = guardianPickerIntent();
                      if (!gp) return undefined;
                      return {
                        intent: gp.intent,
                        guardians: gp.guardians,
                        onPick: (g) => {
                          setGuardianPickerIntent(null);
                          setManualRoute({ name: 'login', account: g, nextIntent: gp.intent });
                        },
                        onCancel: () => setGuardianPickerIntent(null),
                      };
                    })()}
                  />
                );
              })()}
            </Match>

            <Match when={route().name === 'login'}>
              {(() => {
                const r = route();
                if (r.name !== 'login') return null;
                return (
                  <LoginPasswordScreen
                    account={r.account}
                    onLoggedIn={(session) => void completeLogin(r.account, session, r.nextIntent)}
                    onCancel={() => {
                      setManualRoute({ name: 'selection' });
                    }}
                  />
                );
              })()}
            </Match>

            <Match when={route().name === 'subscriptionRequired'}>
              {(() => {
                const r = route();
                if (r.name !== 'subscriptionRequired') return null;
                return (
                  <SubscriptionRequiredScreen
                    account={r.account}
                    session={r.session}
                    cloudSession={r.cloudSession}
                    cloudFamilyId={r.cloudFamilyId}
                    subscribeNow={r.subscribeNow ?? false}
                    platform={mobilePlatform() === 'ios' ? 'ios' : 'android'}
                    onSubscribed={() => void completeLogin(r.account, r.session)}
                    onSignOut={() => {
                      void logoutLocal(r.session.sessionId).finally(() =>
                        setManualRoute({ name: 'selection' }),
                      );
                    }}
                    onDeletionRequested={(deleteAfter) => {
                      void logoutLocal(r.session.sessionId).finally(() => {
                        setCloudMessage(
                          `Trial data deletion is scheduled for ${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(deleteAfter))}.`,
                        );
                        setManualRoute({ name: 'setup' });
                      });
                    }}
                  />
                );
              })()}
            </Match>

            <Match when={route().name === 'restoreDevicePassword'}>
              {(() => {
                const r = route();
                if (r.name !== 'restoreDevicePassword') return null;
                return (
                  <RestoreDevicePasswordScreen
                    displayName={r.account.displayName}
                    onCancel={() => {
                      void clearCloudSignIn();
                      setManualRoute({ name: 'selection' });
                    }}
                    onComplete={async (newPassword) => {
                      const result = await cloudRestoreComplete({
                        localAccountId: r.account.accountId,
                        accessToken: r.cloud.accessToken,
                        publishableKey: getCloudAuthPublishableKey(),
                        supabaseUrl: getCloudAuthProjectUrl(),
                        familyId: r.cloud.bootstrap.family.id,
                        familyAccountId: r.cloud.bootstrap.guardianAccount.id,
                        newPassword,
                      });
                      if (!result.ok) throw new Error(result.error.message);
                      await completeLogin(r.account, result.value);
                    }}
                  />
                );
              })()}
            </Match>

            <Match when={route().name === 'home'}>
              {(() => {
                const r = route();
                if (r.name !== 'home') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    // Preserve the child dashboard sub-view across a pushed
                    // action route.  Resetting this to "chores" while
                    // HomeScreen restores Money/Me left the visible content
                    // and the selected bottom-navigation item disagreeing.
                    initialTab={r.account.roleCode === 'child' ? childHomeTab() : 'home'}
                    showMobileNavigation
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <Show
                      when={r.account.roleCode === 'guardian'}
                      fallback={
                        <HomeScreen
                          account={r.account}
                          sessionId={r.session.sessionId}
                          requestedChildHomeView={
                            r.account.roleCode === 'child' ? childHomeTab() : undefined
                          }
                          flashMessage={r.flashMessage}
                          onFlashDismiss={() =>
                            setManualRoute({
                              name: 'home',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onLogOut={async () => {
                            // Best-effort: tell the backend our session is invalid.
                            // We always route to selection regardless of the result;
                            // the user said "log me out" and that intent is satisfied.
                            // US-AUTH-05.
                            await logoutLocal(r.session.sessionId);
                            setManualRoute({ name: 'selection' });
                          }}
                          onAddChild={() =>
                            setManualRoute({
                              name: 'addChild',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onResetChildPassword={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'resetChildPassword',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onAdjustBucketBalance={(bucket) =>
                            setManualRoute({
                              name: 'adjustBucketBalance',
                              account: r.account,
                              session: r.session,
                              bucket,
                              sourceCode: 'balance_adjustment',
                            })
                          }
                          onDirectTransfer={(childAccountId, childDisplayName, buckets) =>
                            setManualRoute({
                              name: 'guardianBucketTransfer',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                              buckets,
                            })
                          }
                          onViewChildDetail={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'childDetail',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onChildDeactivated={(childDisplayName) =>
                            setManualRoute({
                              name: 'home',
                              account: r.account,
                              session: r.session,
                              flashMessage: `Deactivated ${childDisplayName}.`,
                            })
                          }
                          onRecordDeposit={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'recordDeposit',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onRecordGift={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'recordGift',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onAllocateGift={(depositId) =>
                            setManualRoute({
                              name: 'giftAllocation',
                              account: r.account,
                              session: r.session,
                              depositId,
                            })
                          }
                          onSpend={(bucket) =>
                            setManualRoute({
                              name: 'spend',
                              account: r.account,
                              session: r.session,
                              bucket,
                            })
                          }
                          onRequestWithdrawal={(bucket) =>
                            setManualRoute({
                              name: 'submitPendingWithdrawal',
                              account: r.account,
                              session: r.session,
                              bucket,
                            })
                          }
                          onMoveMoney={(childAccountId, buckets, currencyCode) =>
                            setManualRoute({
                              name: 'moveMoney',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              buckets,
                              currencyCode,
                            })
                          }
                          onViewHistory={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'history',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onViewChores={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'chores',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                            })
                          }
                          onViewMyRequests={() =>
                            setManualRoute({
                              name: 'myRequests',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onViewPendingApprovals={() =>
                            setManualRoute({
                              name: 'pendingApprovals',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onViewPendingApprovalsForChild={(childAccountId) =>
                            setManualRoute({
                              name: 'pendingApprovals',
                              account: r.account,
                              session: r.session,
                              filterChildId: childAccountId,
                            })
                          }
                          onOpenSettings={() =>
                            setManualRoute({
                              name: 'settings',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onOpenAuditLog={() =>
                            setManualRoute({
                              name: 'auditLog',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onAddGuardian={() =>
                            setManualRoute({
                              name: 'addGuardian',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onAddChorePool={() =>
                            setManualRoute({
                              name: 'createChore',
                              account: r.account,
                              session: r.session,
                              childAccountId: r.account.accountId,
                              childDisplayName: r.account.displayName,
                              defaultAnyChild: true,
                              returnRoute: 'home',
                            })
                          }
                          onViewAllChores={() =>
                            setManualRoute({
                              name: 'chores',
                              account: r.account,
                              session: r.session,
                              childAccountId: undefined,
                              childDisplayName: 'All Chores',
                            })
                          }
                          onOpenRoutineTask={(instanceId) =>
                            setManualRoute({
                              name: 'routineTask',
                              account: r.account,
                              session: r.session,
                              instanceId,
                            })
                          }
                          onManageRoutines={() =>
                            setManualRoute({
                              name: 'routineManagement',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onManageAllowances={() =>
                            setManualRoute({
                              name: 'allowance',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onOpenGoals={() =>
                            setManualRoute({
                              name: 'goals',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onOpenGivingGoals={() =>
                            setManualRoute({
                              name: 'givingGoals',
                              account: r.account,
                              session: r.session,
                            })
                          }
                          onOpenStreaks={(childAccountId, childDisplayName) =>
                            setManualRoute({
                              name: 'streakHistory',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                              returnToChildDetail: false,
                            })
                          }
                          onManageBuckets={(childAccountId, childDisplayName, buckets) =>
                            setManualRoute({
                              name: 'editBucketSplit',
                              account: r.account,
                              session: r.session,
                              childAccountId,
                              childDisplayName,
                              buckets,
                            })
                          }
                          onDeactivateBucket={(bucket, existingBuckets) =>
                            setManualRoute({
                              name: 'deactivateBucket',
                              account: r.account,
                              session: r.session,
                              bucket,
                              existingBuckets,
                            })
                          }
                          onDeleteBucket={(bucket, existingBuckets) =>
                            setManualRoute({
                              name: 'deleteBucket',
                              account: r.account,
                              session: r.session,
                              bucket,
                              existingBuckets,
                            })
                          }
                        />
                      }
                    >
                      <MobileGuardianHomeScreen
                        account={r.account}
                        sessionId={r.session.sessionId}
                        flashMessage={r.flashMessage}
                        onFlashDismiss={() =>
                          setManualRoute({
                            name: 'home',
                            account: r.account,
                            session: r.session,
                          })
                        }
                        onAddChild={() =>
                          setManualRoute({
                            name: 'addChild',
                            account: r.account,
                            session: r.session,
                          })
                        }
                        onAddGuardian={() =>
                          setManualRoute({
                            name: 'addGuardian',
                            account: r.account,
                            session: r.session,
                          })
                        }
                        onOpenSettings={() =>
                          setManualRoute({
                            name: 'settings',
                            account: r.account,
                            session: r.session,
                          })
                        }
                        onViewPendingApprovals={() =>
                          setManualRoute({
                            name: 'pendingApprovals',
                            account: r.account,
                            session: r.session,
                          })
                        }
                        onSelectChild={(childAccountId, childDisplayName) =>
                          setManualRoute({
                            name: 'childDetail',
                            account: r.account,
                            session: r.session,
                            childAccountId,
                            childDisplayName,
                          })
                        }
                      />
                    </Show>
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'kids'}>
              {(() => {
                const r = route();
                if (r.name !== 'kids') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    initialTab="kids"
                    showMobileNavigation
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <GuardianKidsScreen
                      account={r.account}
                      sessionId={r.session.sessionId}
                      currencyCode={r.account.displayCurrencyCode}
                      onAddChild={() =>
                        setManualRoute({ name: 'addChild', account: r.account, session: r.session })
                      }
                      onSelectChild={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'childDetail',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnRoute: 'kids',
                        })
                      }
                      onOpenBuckets={(childAccountId, childDisplayName, buckets) =>
                        setManualRoute({
                          name: 'editBucketSplit',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          buckets,
                        })
                      }
                      onOpenChores={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'chores',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                        })
                      }
                      onOpenMoney={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'recordDeposit',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'childDetail'}>
              {(() => {
                const r = route();
                if (r.name !== 'childDetail') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    initialTab="kids"
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <ChildDetailScreen
                      account={r.account}
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      flashMessage={r.flashMessage}
                      onFlashDismiss={() =>
                        setManualRoute({
                          name: 'childDetail',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.childAccountId,
                          childDisplayName: r.childDisplayName,
                          ...(r.returnRoute === undefined ? {} : { returnRoute: r.returnRoute }),
                        })
                      }
                      onBack={() =>
                        setManualRoute({
                          name: r.returnRoute ?? 'home',
                          account: r.account,
                          session: r.session,
                        })
                      }
                      onChildDeactivated={(childDisplayName, purgeAfterUtc) =>
                        setManualRoute({
                          name: r.returnRoute ?? 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Deleted ${childDisplayName}. Their account can be restored until ${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(purgeAfterUtc))}.`,
                        })
                      }
                      onViewPendingApprovalsForChild={(childAccountId) =>
                        setManualRoute({
                          name: 'pendingApprovals',
                          account: r.account,
                          session: r.session,
                          filterChildId: childAccountId,
                        })
                      }
                      onRecordDeposit={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'recordDeposit',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onRecordGift={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'recordGift',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onManageAllowances={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'allowance',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onViewHistory={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'history',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onViewChores={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'chores',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onViewStreaks={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'streakHistory',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                        })
                      }
                      onResetChildPassword={(childAccountId, childDisplayName) =>
                        setManualRoute({
                          name: 'resetChildPassword',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onDeactivateBucket={(bucket, existingBuckets) =>
                        setManualRoute({
                          name: 'deactivateBucket',
                          account: r.account,
                          session: r.session,
                          bucket,
                          existingBuckets,
                        })
                      }
                      onAdjustBucketBalance={(bucket) =>
                        setManualRoute({
                          name: 'adjustBucketBalance',
                          account: r.account,
                          session: r.session,
                          bucket,
                          sourceCode: 'balance_adjustment',
                        })
                      }
                      onDirectTransfer={(childAccountId, childDisplayName, buckets) =>
                        setManualRoute({
                          name: 'guardianBucketTransfer',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          buckets,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                      onManageBuckets={(childAccountId, childDisplayName, buckets) =>
                        setManualRoute({
                          name: 'editBucketSplit',
                          account: r.account,
                          session: r.session,
                          childAccountId,
                          childDisplayName,
                          buckets,
                          returnToChildDetail: true,
                          ...(r.returnRoute === undefined
                            ? {}
                            : { childDetailReturnRoute: r.returnRoute }),
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'resetChildPassword'}>
              {(() => {
                const r = route();
                if (r.name !== 'resetChildPassword') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <ResetChildPasswordScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      onReset={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                flashMessage: `Reset ${r.childDisplayName}'s password.`,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : {
                                name: 'home',
                                account: r.account,
                                session: r.session,
                                flashMessage: `Reset ${r.childDisplayName}'s password.`,
                              },
                        )
                      }
                      onCancel={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'recordDeposit'}>
              {(() => {
                const r = route();
                if (r.name !== 'recordDeposit') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <RecordDepositScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      currencyCode={r.account.displayCurrencyCode}
                      onDeposited={(result) =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                flashMessage: `Recorded deposit of ${formatMinorUnits(result.amountMinorUnits, r.account.displayCurrencyCode)}.`,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : {
                                name: 'home',
                                account: r.account,
                                session: r.session,
                                flashMessage: `Recorded deposit of ${formatMinorUnits(result.amountMinorUnits, r.account.displayCurrencyCode)} for ${r.childDisplayName}.`,
                              },
                        )
                      }
                      onCancel={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'spend'}>
              {(() => {
                const r = route();
                if (r.name !== 'spend') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <SpendScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      currencyCode={r.account.displayCurrencyCode}
                      onSpent={(result) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Spent ${formatMinorUnits(result.amountMinorUnits, r.account.displayCurrencyCode)} from ${result.bucketName}.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'submitPendingWithdrawal'}>
              {(() => {
                const r = route();
                if (r.name !== 'submitPendingWithdrawal') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <SubmitPendingWithdrawalScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      currencyCode={r.account.displayCurrencyCode}
                      onSubmitted={(result) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Requested ${formatMinorUnits(result.amountMinorUnits, r.account.displayCurrencyCode)} from ${result.bucketName}. Waiting for your guardian to approve.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'moveMoney'}>
              {(() => {
                const r = route();
                if (r.name !== 'moveMoney') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <MoveMoneyScreen
                      sessionId={r.session.sessionId}
                      buckets={r.buckets}
                      currencyCode={r.currencyCode}
                      onSubmitted={(result) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Requested ${formatMinorUnits(result.amountMinorUnits, r.currencyCode)} from ${result.sourceBucketName} to ${result.targetBucketName}. Waiting for your guardian to approve.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'history'}>
              {(() => {
                const r = route();
                if (r.name !== 'history') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <HistoryScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      currencyCode={r.account.displayCurrencyCode}
                      isGuardian={r.account.roleCode === 'guardian'}
                      onBack={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'auditLog'}>
              {(() => {
                const r = route();
                if (r.name !== 'auditLog') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <AuditLogScreen
                      sessionId={r.session.sessionId}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'chores'}>
              {(() => {
                const r = route();
                if (r.name !== 'chores') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    initialTab="chores"
                    showMobileNavigation={
                      r.account.roleCode === 'guardian' && r.childAccountId === undefined
                    }
                    showTabletRail={
                      r.account.roleCode === 'guardian' && r.childAccountId === undefined
                    }
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <ChoresScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      currencyCode={r.account.displayCurrencyCode}
                      canCreate={r.account.roleCode === 'guardian'}
                      canClaim={
                        r.account.roleCode === 'child' && r.account.accountId === r.childAccountId
                      }
                      canDeactivate={r.account.roleCode === 'guardian'}
                      canEdit={r.account.roleCode === 'guardian'}
                      topLevel={r.account.roleCode === 'guardian' && r.childAccountId === undefined}
                      onAddChore={() =>
                        setManualRoute({
                          name: 'createChore',
                          account: r.account,
                          session: r.session,
                          // A top-level Chores page has no selected child.
                          // CreateChoreScreen now presents a real child selector;
                          // the guardian id is only an initial placeholder and can
                          // never be submitted as the target child.
                          childAccountId: r.childAccountId ?? r.account.accountId,
                          childDisplayName: r.childDisplayName,
                          returnRoute: 'chores',
                          returnChoreScope: {
                            childAccountId: r.childAccountId,
                            childDisplayName: r.childDisplayName,
                          },
                        })
                      }
                      onEditChore={(chore) =>
                        r.childAccountId !== undefined &&
                        setManualRoute({
                          name: 'editChore',
                          account: r.account,
                          session: r.session,
                          chore,
                          childAccountId: r.childAccountId,
                          childDisplayName: r.childDisplayName,
                        })
                      }
                      onBack={() =>
                        setManualRoute(
                          r.returnToChildDetail && r.childAccountId !== undefined
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'createChore'}>
              {(() => {
                const r = route();
                if (r.name !== 'createChore') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <CreateChoreScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      currencyCode={r.account.displayCurrencyCode}
                      defaultAnyChild={r.defaultAnyChild ?? false}
                      onCreated={(_created) =>
                        r.returnRoute === 'home' || r.defaultAnyChild
                          ? setManualRoute({ name: 'home', account: r.account, session: r.session })
                          : setManualRoute({
                              name: 'chores',
                              account: r.account,
                              session: r.session,
                              childAccountId: r.returnChoreScope
                                ? r.returnChoreScope.childAccountId
                                : r.childAccountId,
                              childDisplayName: r.returnChoreScope
                                ? r.returnChoreScope.childDisplayName
                                : r.childDisplayName,
                            })
                      }
                      onCancel={() =>
                        r.returnRoute === 'home' || r.defaultAnyChild
                          ? setManualRoute({ name: 'home', account: r.account, session: r.session })
                          : setManualRoute({
                              name: 'chores',
                              account: r.account,
                              session: r.session,
                              childAccountId: r.returnChoreScope
                                ? r.returnChoreScope.childAccountId
                                : r.childAccountId,
                              childDisplayName: r.returnChoreScope
                                ? r.returnChoreScope.childDisplayName
                                : r.childDisplayName,
                            })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'editChore'}>
              {(() => {
                const r = route();
                if (r.name !== 'editChore') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <EditChoreScreen
                      sessionId={r.session.sessionId}
                      chore={r.chore}
                      childDisplayName={r.childDisplayName}
                      currencyCode={r.account.displayCurrencyCode}
                      onSaved={(_updated) =>
                        setManualRoute({
                          name: 'chores',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.childAccountId,
                          childDisplayName: r.childDisplayName,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({
                          name: 'chores',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.childAccountId,
                          childDisplayName: r.childDisplayName,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'submitPendingDeposit'}>
              {(() => {
                const r = route();
                if (r.name !== 'submitPendingDeposit') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <SubmitPendingDepositScreen
                      sessionId={r.session.sessionId}
                      currencyCode={r.account.displayCurrencyCode}
                      onSubmitted={(result) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Submitted ${formatMinorUnits(result.amountMinorUnits, r.account.displayCurrencyCode)} for guardian approval.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'myRequests'}>
              {(() => {
                const r = route();
                if (r.name !== 'myRequests') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <MyPendingRequestsScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.account.accountId}
                      currencyCode={r.account.displayCurrencyCode}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                      onViewChores={() =>
                        setManualRoute({
                          name: 'chores',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.account.accountId,
                          childDisplayName: r.account.displayName,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'pendingApprovals'}>
              {(() => {
                const r = route();
                if (r.name !== 'pendingApprovals') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    initialTab="approvals"
                    showMobileNavigation
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <PendingApprovalsScreen
                      sessionId={r.session.sessionId}
                      currencyCode={r.account.displayCurrencyCode}
                      filterChildId={r.name === 'pendingApprovals' ? r.filterChildId : undefined}
                      onOpenHistory={() =>
                        setManualRoute({
                          name: 'auditLog',
                          account: r.account,
                          session: r.session,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'addChild'}>
              {(() => {
                const r = route();
                if (r.name !== 'addChild') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <AddChildScreen
                      sessionId={r.session.sessionId}
                      familyCurrencyCode={r.account.displayCurrencyCode === 'USD' ? 'USD' : 'GBP'}
                      onCreated={(child) =>
                        // US-BUCKET-02 -- interstitial review-buckets
                        // step before returning to home. The new
                        // child's default buckets (Spend / Save /
                        // Tithe) are already created by the backend
                        // child_create flow; this screen lets the
                        // guardian rename, change percent, or remove
                        // any of them before the child starts using
                        // them. The flash message moves to the
                        // ReviewBucketsScreen -> home transition.
                        setManualRoute({
                          name: 'reviewBuckets',
                          account: r.account,
                          session: r.session,
                          childAccountId: child.accountId,
                          childDisplayName: child.displayName,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* US-BUCKET-02 -- review-buckets interstitial after child
                creation. The screen reuses the existing
                bucket_update / bucket_deactivate commands; no new
                backend surface needed. */}
            <Match when={route().name === 'reviewBuckets'}>
              {(() => {
                const r = route();
                if (r.name !== 'reviewBuckets') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <ReviewBucketsScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      currency={r.account.displayCurrencyCode}
                      onDone={() =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Added ${r.childDisplayName} to your family.`,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'addGuardian'}>
              {(() => {
                const r = route();
                if (r.name !== 'addGuardian') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <AddGuardianScreen
                      sessionId={r.session.sessionId}
                      familyCurrencyCode={r.account.displayCurrencyCode === 'USD' ? 'USD' : 'GBP'}
                      onCreated={(guardian) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Added ${guardian.displayName} as a guardian. They can sign in from the account selection screen.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'addBucket'}>
              {(() => {
                const r = route();
                if (r.name !== 'addBucket') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <AddBucketScreen
                      sessionId={r.session.sessionId}
                      actorIsChild={r.account.roleCode === 'child'}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      existingBuckets={r.existingBuckets}
                      onCreated={(out) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Added bucket for ${r.childDisplayName} (bucket #${out.newBucketId}).`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'editBucket'}>
              {(() => {
                const r = route();
                if (r.name !== 'editBucket') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <EditBucketScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      existingBuckets={r.existingBuckets}
                      currency={r.account.displayCurrencyCode}
                      actorIsChild={r.account.roleCode === 'child'}
                      onSaved={(out) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `Updated '${r.bucket.bucketName}' (bucket #${out.bucketId}).`,
                        })
                      }
                      onBalanceAdjusted={(_newBalance) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `${r.bucket.bucketName} balance updated.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                      onFixSplits={() =>
                        setManualRoute({
                          name: 'editBucketSplit',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.existingBuckets[0]?.childAccountId ?? 0,
                          childDisplayName: r.existingBuckets[0]?.childDisplayName ?? 'this child',
                          buckets: r.existingBuckets,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'deactivateBucket'}>
              {(() => {
                const r = route();
                if (r.name !== 'deactivateBucket') return null;
                const childName =
                  r.existingBuckets.find((b) => b.bucketId === r.bucket.bucketId)
                    ?.childDisplayName ?? 'this child';
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <DeactivateBucketScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      childDisplayName={childName}
                      siblings={r.existingBuckets}
                      currencyCode={r.account.displayCurrencyCode}
                      actorIsChild={r.account.roleCode === 'child'}
                      onDeactivated={(out) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage:
                            out.movedAmountMinorUnits > 0
                              ? `Deactivated '${r.bucket.bucketName}' and moved the balance.`
                              : `Deactivated '${r.bucket.bucketName}'.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'deleteBucket'}>
              {(() => {
                const r = route();
                if (r.name !== 'deleteBucket') return null;
                const childName =
                  r.existingBuckets.find((b) => b.bucketId === r.bucket.bucketId)
                    ?.childDisplayName ?? 'this child';
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <DeleteBucketScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      childDisplayName={childName}
                      siblings={r.existingBuckets}
                      currencyCode={r.account.displayCurrencyCode}
                      onSubmitted={(out) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage:
                            out.amountMinorUnits > 0
                              ? `Asked a guardian to delete '${r.bucket.bucketName}' and move the balance.`
                              : `Asked a guardian to delete '${r.bucket.bucketName}'.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-BUCKET-15 / US-BUCKET-17 -- adjust bucket balance ---- */}
            <Match when={route().name === 'adjustBucketBalance'}>
              {(() => {
                const r = route();
                if (r.name !== 'adjustBucketBalance') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <BucketAdjustBalanceScreen
                      sessionId={r.session.sessionId}
                      bucket={r.bucket}
                      sourceCode={r.sourceCode}
                      currency={r.account.displayCurrencyCode}
                      onSaved={(_newBalance) =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: `${r.bucket.bucketName} balance updated.`,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-BUCKET-16 -- guardian direct inter-bucket transfer ---- */}
            <Match when={route().name === 'guardianBucketTransfer'}>
              {(() => {
                const r = route();
                if (r.name !== 'guardianBucketTransfer') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <GuardianBucketTransferScreen
                      sessionId={r.session.sessionId}
                      buckets={r.buckets}
                      currency={r.account.displayCurrencyCode}
                      {...(r.initialSourceBucketId !== undefined
                        ? { initialSourceBucketId: r.initialSourceBucketId }
                        : {})}
                      onSaved={(_src, _tgt, _amount) =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                flashMessage: 'Transfer complete.',
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : {
                                name: 'home',
                                account: r.account,
                                session: r.session,
                                flashMessage: 'Transfer complete.',
                              },
                        )
                      }
                      onCancel={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-BUCKET-18 -- edit deposit split percentages ---- */}
            <Match when={route().name === 'editBucketSplit'}>
              {(() => {
                const r = route();
                if (r.name !== 'editBucketSplit') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <BucketManagerScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      buckets={r.buckets}
                      isGuardian={r.account.roleCode === 'guardian'}
                      currency={r.account.displayCurrencyCode}
                      onSaved={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                flashMessage: `Deposit split updated for ${r.childDisplayName}.`,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : {
                                name: 'home',
                                account: r.account,
                                session: r.session,
                                flashMessage: `Deposit split updated for ${r.childDisplayName}.`,
                              },
                        )
                      }
                      onCancel={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                      onAddBucket={() =>
                        setManualRoute({
                          name: 'addBucket',
                          account: r.account,
                          session: r.session,
                          childAccountId: r.childAccountId,
                          childDisplayName: r.childDisplayName,
                          existingBuckets: r.buckets,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'settings'}>
              {(() => {
                const r = route();
                if (r.name !== 'settings') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({
                        name: 'selection',
                        welcomeMessage:
                          'You were signed out after being idle. Please sign in again.',
                      })
                    }
                  >
                    <SettingsScreen
                      sessionId={r.session.sessionId}
                      accountId={r.account.accountId}
                      displayName={r.account.displayName}
                      roleCode={r.account.roleCode}
                      familyCurrencyCode={r.account.displayCurrencyCode === 'USD' ? 'USD' : 'GBP'}
                      onClose={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                      onAddChild={() =>
                        setManualRoute({ name: 'addChild', account: r.account, session: r.session })
                      }
                      onAddGuardian={() =>
                        setManualRoute({
                          name: 'addGuardian',
                          account: r.account,
                          session: r.session,
                        })
                      }
                      onFamilyDeletionRequested={(deleteAfter) => {
                        void logoutLocal(r.session.sessionId).finally(() => {
                          setCloudMessage(
                            `Deletion scheduled. Your family data can be recovered until ${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(deleteAfter))}. Sign in with a verified guardian identity to recover it.`,
                          );
                          setManualRoute({
                            name: 'setup',
                          });
                        });
                      }}
                      onFamilyCurrencyChanged={(newCurrencyCode) => {
                        void logoutLocal(r.session.sessionId).finally(() =>
                          setManualRoute({
                            name: 'selection',
                            welcomeMessage: `Everyone now uses ${newCurrencyCode}. Sign in again to refresh the family ledger.`,
                          }),
                        );
                      }}
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'tutorials'}>
              {(() => {
                const r = route();
                if (r.name !== 'tutorials') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <TutorialsScreen
                      roleCode={r.account.roleCode}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'reportConcern'}>
              {(() => {
                const r = route();
                if (r.name !== 'reportConcern') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <ReportConcernScreen
                      sessionId={r.session.sessionId}
                      {...(r.context ? { context: r.context } : {})}
                      onBack={() => {
                        const previous = reportReturnRoute();
                        setReportReturnRoute(null);
                        setManualRoute(
                          previous ?? { name: 'home', account: r.account, session: r.session },
                        );
                      }}
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'feedback'}>
              {(() => {
                const r = route();
                if (r.name !== 'feedback') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <FeedbackScreen
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'familyContentRules'}>
              {(() => {
                const r = route();
                if (r.name !== 'familyContentRules') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <FamilyContentRulesScreen
                      sessionId={r.session.sessionId}
                      roleCode={r.account.roleCode}
                      policyVersion={r.policyVersion}
                      onAccepted={() => completeLogin(r.account, r.session, r.nextIntent)}
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ROUTINE-08 -- Child and guardian streak-day history ---- */}
            <Match when={route().name === 'streakHistory'}>
              {(() => {
                const r = route();
                if (r.name !== 'streakHistory') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <StreakHistoryScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      viewerRole={r.account.roleCode}
                      onBack={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ROUTINE-06 -- Routine management (guardian) ---- */}
            <Match when={route().name === 'routineManagement'}>
              {(() => {
                const r = route();
                if (r.name !== 'routineManagement') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    initialTab="routines"
                    showMobileNavigation
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <RoutineManagementScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      topLevel
                      onAddRoutine={(children, buckets) => {
                        const firstChild = children[0];
                        if (!firstChild) return;
                        setManualRoute({
                          name: 'createRoutine',
                          account: r.account,
                          session: r.session,
                          child: firstChild,
                          childBuckets: buckets,
                          allChildren: children,
                        });
                      }}
                      onEditRoutine={(task, child, childBuckets) => {
                        setManualRoute({
                          name: 'createRoutine',
                          account: r.account,
                          session: r.session,
                          child,
                          childBuckets,
                          routine: task,
                        });
                      }}
                      onViewHistory={(routineTask) =>
                        setManualRoute({
                          name: 'routineHistory',
                          account: r.account,
                          session: r.session,
                          task: routineTask,
                        })
                      }
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ROUTINE-01 -- Create routine (guardian) ---- */}
            <Match when={route().name === 'createRoutine'}>
              {(() => {
                const r = route();
                if (r.name !== 'createRoutine') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <CreateRoutineScreen
                      sessionId={r.session.sessionId}
                      child={r.child}
                      childBuckets={r.childBuckets}
                      {...(r.allChildren ? { allChildren: r.allChildren } : {})}
                      {...(r.routine ? { routine: r.routine } : {})}
                      onSaved={() =>
                        setManualRoute({
                          name: 'routineManagement',
                          account: r.account,
                          session: r.session,
                        })
                      }
                      onCancel={() =>
                        setManualRoute({
                          name: 'routineManagement',
                          account: r.account,
                          session: r.session,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ROUTINE-07 -- Routine history (guardian + child) ---- */}
            <Match when={route().name === 'routineHistory'}>
              {(() => {
                const r = route();
                if (r.name !== 'routineHistory') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <RoutineHistoryScreen
                      sessionId={r.session.sessionId}
                      task={r.task}
                      currency={r.account.displayCurrencyCode}
                      onBack={() =>
                        setManualRoute({
                          name: 'routineManagement',
                          account: r.account,
                          session: r.session,
                        })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ROUTINE-03 -- Child completes a routine task ---- */}
            <Match when={route().name === 'routineTask'}>
              {(() => {
                const r = route();
                if (r.name !== 'routineTask') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <RoutineTaskLoader
                      sessionId={r.session.sessionId}
                      instanceId={r.instanceId}
                      currency={r.account.displayCurrencyCode}
                      onDone={() =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: 'Task submitted!',
                        })
                      }
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-ALLOWANCE-01 -- Recurring allowance (guardian) ---- */}
            <Match when={route().name === 'allowance'}>
              {(() => {
                const r = route();
                if (r.name !== 'allowance') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <AllowanceScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      initialChildAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      onBack={() =>
                        setManualRoute(
                          r.childAccountId && r.childDisplayName
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-GOAL-01/02 -- Savings goals (child) ---- */}
            <Match when={route().name === 'goals'}>
              {(() => {
                const r = route();
                if (r.name !== 'goals') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    // Goals is a child destination, not a pushed action. Keep
                    // the common child chrome visible so the page has the
                    // hamburger/top controls and the Chores/Money/Goals/Me
                    // quick navigation just like the other child tabs.
                    initialTab="goals"
                    showMobileNavigation
                    showTabletRail
                    accountDisplayName={r.account.displayName}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <GoalsScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-REPORT-01 -- Monthly family statement ---- */}
            <Match when={route().name === 'statement'}>
              {(() => {
                const r = route();
                if (r.name !== 'statement') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <MonthlyStatementScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-CUSTOM-01 -- Bucket templates ---- */}
            <Match when={route().name === 'bucketTemplates'}>
              {(() => {
                const r = route();
                if (r.name !== 'bucketTemplates') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <BucketTemplatesScreen
                      sessionId={r.session.sessionId}
                      onBack={() =>
                        setManualRoute({ name: 'settings', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-REPORT-03 -- Chore trends ---- */}
            <Match when={route().name === 'choreTrends'}>
              {(() => {
                const r = route();
                if (r.name !== 'choreTrends') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <ChoreTrendsScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-GIVE-01 -- Giving goals (child) ---- */}
            <Match when={route().name === 'givingGoals'}>
              {(() => {
                const r = route();
                if (r.name !== 'givingGoals') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <GoalsScreen
                      sessionId={r.session.sessionId}
                      currency={r.account.displayCurrencyCode}
                      goalType="giving"
                      onBack={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'recordGift'}>
              {(() => {
                const r = route();
                if (r.name !== 'recordGift') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <RecordGiftScreen
                      sessionId={r.session.sessionId}
                      childAccountId={r.childAccountId}
                      childDisplayName={r.childDisplayName}
                      displayCurrencyCode={r.account.displayCurrencyCode}
                      onDone={(_depositId, autoAllocated) =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                flashMessage: autoAllocated
                                  ? 'Gift recorded and auto-allocated!'
                                  : `Gift recorded -- waiting for ${r.childDisplayName} to allocate.`,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : {
                                name: 'home',
                                account: r.account,
                                session: r.session,
                                flashMessage: autoAllocated
                                  ? 'Gift recorded and auto-allocated!'
                                  : `Gift recorded -- waiting for ${r.childDisplayName} to allocate.`,
                              },
                        )
                      }
                      onCancel={() =>
                        setManualRoute(
                          r.returnToChildDetail
                            ? {
                                name: 'childDetail',
                                account: r.account,
                                session: r.session,
                                childAccountId: r.childAccountId,
                                childDisplayName: r.childDisplayName,
                                ...(r.childDetailReturnRoute === undefined
                                  ? {}
                                  : { returnRoute: r.childDetailReturnRoute }),
                              }
                            : { name: 'home', account: r.account, session: r.session },
                        )
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            {/* ---- US-GIFT-02/03 -- Gift allocation (child) ---- */}
            <Match when={route().name === 'giftAllocation'}>
              {(() => {
                const r = route();
                if (r.name !== 'giftAllocation') return null;
                return (
                  <AuthenticatedShell
                    sessionId={r.session.sessionId}
                    initialExpiresAtUtc={r.session.expiresAtUtc}
                    showMobileNavigation={false}
                    onSessionExpired={() =>
                      setManualRoute({ name: 'selection', welcomeMessage: 'Session expired.' })
                    }
                  >
                    <GiftAllocationScreen
                      sessionId={r.session.sessionId}
                      {...(r.depositId !== undefined ? { depositId: r.depositId } : {})}
                      onDone={() =>
                        setManualRoute({
                          name: 'home',
                          account: r.account,
                          session: r.session,
                          flashMessage: 'Gift allocated!',
                        })
                      }
                      onCancel={() =>
                        setManualRoute({ name: 'home', account: r.account, session: r.session })
                      }
                    />
                  </AuthenticatedShell>
                );
              })()}
            </Match>

            <Match when={route().name === 'error'}>
              {(() => {
                const r = route();
                return (
                  <main class="app-error">
                    <h1>Something went wrong</h1>
                    <p>{r.name === 'error' ? r.message : 'Unknown error.'}</p>
                    <p>Please close the app and try again.</p>
                  </main>
                );
              })()}
            </Match>
          </Switch>
        </div>
      </div>
    </>
  );
};

function describeCloudSignInError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (error && typeof error === 'object') {
    const value = error as Record<string, unknown>;
    for (const key of ['message', 'error', 'reason', 'details']) {
      const message = value[key];
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
    try {
      const serialized = JSON.stringify(error);
      if (serialized && serialized !== '{}') return serialized;
    } catch {
      // Keep the stable fallback below for unserialisable native errors.
    }
  }
  return 'Cloud sign-in could not start.';
}

function formatMinorUnits(minorUnits: number, currencyCode: string): string {
  const major = minorUnits / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(major);
  } catch {
    return `${currencyCode} ${major.toFixed(2)}`;
  }
}
